import { useCopyPaste } from "@features/builder/graph/contextMenu/useCopyPaste"
import { createEmptyNode, findMaxId } from "@features/builder/graph/helpers"
import { AppNode, NodeType } from "@features/builder/graph/types"
import { KeyBinding } from "@features/keymap/KeyBinding"
import { formatModifierKey } from "@features/keymap/keyShortcutFormatter"
import { shortcuts } from "@features/keymap/shortcuts"
import { useElementAdjustedPosition } from "@hooks/useElementAdjustedPosition"
import {
  AddBoxOutlined,
  AddCircleOutline,
  CancelOutlined,
  ContentCopy,
  ContentPaste,
  TimelineOutlined,
} from "@mui/icons-material"
import {
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Paper,
  Typography,
} from "@mui/material"
import { nextDrawerNodeIdAtom } from "@features/builder/graph/graphAtoms"
import { useReactFlow, useStoreApi } from "@xyflow/react"
import { useRef } from "react"
import { useSetAtom } from "jotai"

export type ContextPaneMenuProps = {
  x: number
  y: number
  onCloseClick: () => void
  onOpenEdgesDialog?: () => void
}

export default function ContextPaneMenu({
  x,
  y,
  onCloseClick,
  onOpenEdgesDialog,
}: ContextPaneMenuProps) {
  const { addNodes, getNodes, screenToFlowPosition } = useReactFlow<AppNode>()
  const setNextDrawerNodeId = useSetAtom(nextDrawerNodeIdAtom)
  const { handleCopyNodes, handlePasteNodes } = useCopyPaste(onCloseClick)
  const flowClickPosition = screenToFlowPosition({ x, y })
  const store = useStoreApi<AppNode>()

  const menuRef = useRef<HTMLDivElement>(null)
  const menuPosition = useElementAdjustedPosition({ ref: menuRef, x, y })

  const handleAddNode = () => {
    const nodes = getNodes()
    const maxId = findMaxId(nodes, false)

    addNodes(createEmptyNode(NodeType.Empty, maxId, flowClickPosition, true))
    onCloseClick()

    store.getState().resetSelectedElements()
    store.getState().addSelectedNodes([maxId])
    setNextDrawerNodeId(maxId)
  }

  const handleAddGroup = () => {
    const nodes = getNodes()

    const maxId = findMaxId(nodes, true)
    addNodes(createEmptyNode(NodeType.Group, maxId, flowClickPosition))
    onCloseClick()
  }

  const handlePaste = () => {
    handlePasteNodes(flowClickPosition)
    onCloseClick()
  }

  const handleCopy = () => {
    handleCopyNodes()
    onCloseClick()
  }

  return (
    <Paper sx={{ position: "fixed", zIndex: 10, ...menuPosition }} ref={menuRef}>
      <MenuList dense>
        <MenuItem onClick={handleAddNode} data-testid="pane-menu-add-node-option">
          <ListItemIcon>
            <AddCircleOutline fontSize="small" />
          </ListItemIcon>
          <ListItemText>Add node</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleAddGroup} data-testid="pane-menu-add-group-option">
          <ListItemIcon>
            <AddBoxOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Add group</ListItemText>
        </MenuItem>

        <MenuItem onClick={onOpenEdgesDialog} data-testid="pane-menu-create-edges-option">
          <ListItemIcon>
            <TimelineOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Create edges</ListItemText>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
            {formatModifierKey("ctrl")}E
          </Typography>
        </MenuItem>

        <Divider />

        <MenuItem onClick={handleCopy} data-testid="pane-menu-copy-option">
          <ListItemIcon>
            <ContentCopy fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy</ListItemText>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
            {formatModifierKey("ctrl")}C
          </Typography>
        </MenuItem>

        <MenuItem onClick={handlePaste} data-testid="pane-menu-paste-option">
          <ListItemIcon>
            <ContentPaste fontSize="small" />
          </ListItemIcon>
          <ListItemText>Paste</ListItemText>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
            {formatModifierKey("ctrl")}V
          </Typography>
        </MenuItem>

        <Divider />

        <MenuItem onClick={onCloseClick} data-testid="pane-menu-close-option">
          <ListItemIcon>
            <CancelOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Cancel</ListItemText>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
            ESC
          </Typography>
          <KeyBinding bind={shortcuts["close-pane-menu"]} onKeyDown={onCloseClick} />
        </MenuItem>
      </MenuList>
    </Paper>
  )
}
